import React, { FC, useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import Variables from '../../design-system/variables';
import Colours from '../../design-system/colours';
import OvLoadingIndicator from '../UI/atoms/OvLoadingIndicator';
import OvDateParser from '../UI/atoms/OvDateParser';
import OvScanResults from '../UI/organisms/OvScanResults';
import OvPrettyUserName from '../UI/atoms/OvPrettyUserName';
import { Alert, AlertTitle, Autocomplete, TextField } from '@mui/material';
import { parseISO } from 'date-fns';
import OvBackButton from '../UI/atoms/OvBackButton';
import { Resource } from '../../common/model/type/resource.enum';
import useAuthorized from '../../hooks/use-authorized';
import OvCompactButton from '../UI/atoms/OvCompactButton';
import OvRecordDetailsCanvas from '../UI/atoms/OvRecordDetailsCanvas';
import OvStatusChip from '../UI/atoms/OvStatusChip';
import OvCopyToClipboardButton from '../UI/molecules/OvCopyToClipboardButton';
import OvListStateMessage from '../UI/molecules/OvListStateMessage';
import OvRecordDetailsSection from '../UI/molecules/OvRecordDetailsSection';
import OvRecordHeaderActions from '../UI/molecules/OvRecordHeaderActions';
import OvRecordDetailsHeader from '../UI/organisms/OvRecordDetailsHeader';
import WarningIcon from '@mui/icons-material/Warning';
import TrackChangesIcon from '@mui/icons-material/TrackChanges';
import Accordion from '@mui/material/Accordion';
import AccordionSummary from '@mui/material/AccordionSummary';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import AccordionDetails from '@mui/material/AccordionDetails';
import JSONInput from 'react-json-editor-ajrm';
// @ts-ignore
import locale from 'react-json-editor-ajrm/locale/en';
import { ScansService } from '../../services/scans.service';
import { useQuery } from '@tanstack/react-query';
import OvErrorMessage from '../UI/atoms/OvErrorMessage';
import { useAdminScanMutations } from '../../hooks/use-admin-scan-mutations';

const ScanDetails: FC = () => {
  const { t } = useTranslation();
  const { scanId }: any = useParams();
  const [toolkitVersion, setToolkitVersion] = useState('');
  const applicationOperations = useAuthorized(Resource.Application);
  const userOperations = useAuthorized(Resource.User);
  const scanOperations = useAuthorized(Resource.Scan);

  const userReadAllowed =
    userOperations.read || applicationOperations.supervise;
  const scanUpdateAllowed =
    scanOperations.update || applicationOperations.supervise;

  const {
    data: scan,
    isPending: isGetScanPending,
    isFetching: isGetScanFetching,
    error: getScanError,
    refetch: refetchScan,
  } = useQuery({
    queryKey: ['admin-scan', scanId],
    queryFn: () => ScansService.findScanById(scanId),
    retry: 3,
    enabled: !!scanId,
  });

  const {
    data: toolkitVersions,
    isPending: isToolkitVersionsPending,
    isFetching: isToolkitVersionsFetching,
    error: getToolkitVersionsError,
  } = useQuery({
    queryKey: ['admin-scan-toolkit-versions'],
    queryFn: () => ScansService.loadToolkitVersions(),
    retry: 3,
  });

  const { updateScan, processScan } = useAdminScanMutations();

  useEffect(() => {
    if (scan) {
      setToolkitVersion(scan.toolkit_output?.version || '');
    }
  }, [scan]);

  const onEditScan = async (editObject: any) => {
    if (scan?.id) {
      await updateScan.mutateAsync({
        scanId: scan.id,
        updatedProperties: {
          day: scan.day,
          ...editObject,
        },
      });
    }
  };

  const isLoading =
    isGetScanPending ||
    isGetScanFetching ||
    isToolkitVersionsPending ||
    isToolkitVersionsFetching ||
    updateScan.isPending ||
    processScan.isPending;

  if (!scan) {
    return (
      <Page>
        {getScanError ? (
          <OvRecordDetailsCanvas>
            <OvListStateMessage
              isAlert
              icon={<WarningIcon />}
              title={t('scanList.details.errorTitle')}
              description={getScanError.message}
            >
              <StateActions>
                <OvBackButton label={t('app.subPages.menu.scans')} />
                <OvCompactButton onClick={() => refetchScan()}>
                  {t('common.actions.retry')}
                </OvCompactButton>
              </StateActions>
            </OvListStateMessage>
          </OvRecordDetailsCanvas>
        ) : (
          <OvLoadingIndicator position="fixed" />
        )}
      </Page>
    );
  }

  const errors = [
    getScanError,
    getToolkitVersionsError,
    updateScan.error,
    processScan.error,
  ].filter((error): error is Error => !!error);

  return (
    <Page aria-busy={isLoading}>
      {errors.map((error) => (
        <OvErrorMessage key={error.message} message={error.message} />
      ))}

      <OvRecordDetailsCanvas>
        <OvRecordDetailsHeader
          breadcrumbLabel={t('scanList.details.breadcrumb')}
          backControl={<OvBackButton label={t('app.subPages.menu.scans')} />}
          current={scan.id}
          title={
            scan.day ? (
              <OvDateParser
                date={parseISO(scan.day as string)}
                format={'dddd, MMMM DD, YYYY'}
              />
            ) : (
              t('scanList.details.untitled')
            )
          }
          titleAside={
            scan.is_demo_scan && (
              <OvStatusChip label={t('scans.demo')} tone="caution" />
            )
          }
          meta={
            <>
              {scan.user && (
                <Link to={`/users/${scan.user.id}`}>
                  <OvPrettyUserName
                    user={scan.user}
                    options={{ readSafe: !userReadAllowed }}
                  />
                </Link>
              )}
              {scan.id && (
                <span>
                  {t('scanList.details.id')} {scan.id}
                  <OvCopyToClipboardButton
                    text={scan.id}
                    label={t('scanList.details.copyId')}
                  />
                </span>
              )}
              {scan.toolkit_output?.version && (
                <span>
                  {t('scanList.toolkitVersion', {
                    version: scan.toolkit_output.version,
                  })}
                </span>
              )}
            </>
          }
          actions={
            scanUpdateAllowed &&
            scan.input_image_url && (
              <OvRecordHeaderActions>
                <ToolkitVersionField
                  freeSolo
                  options={toolkitVersions ?? []}
                  value={toolkitVersion}
                  onChange={(_, newValue) =>
                    setToolkitVersion((newValue as string) || '')
                  }
                  onInputChange={(_, newInputValue) =>
                    setToolkitVersion(newInputValue)
                  }
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label={t('common.scanDetails.results.toolkitVersion')}
                      size="small"
                    />
                  )}
                />
                <OvCompactButton
                  icon={<TrackChangesIcon />}
                  onClick={() =>
                    processScan.mutateAsync({ scanId, toolkitVersion })
                  }
                >
                  {t('common.scanDetails.process')}
                </OvCompactButton>
              </OvRecordHeaderActions>
            )
          }
        />

        {scan.is_demo_scan && (
          <StyledAlert severity="warning">
            <AlertTitle>{t('common.scanDetails.demoAlertTitle')}</AlertTitle>
            {t('common.scanDetails.demoAlertDesc')}
          </StyledAlert>
        )}

        <OvRecordDetailsSection title={t('scanList.details.resultsSection')}>
          <OvScanResults scan={scan} onSaveEditedScan={onEditScan} />
        </OvRecordDetailsSection>

        <ToolkitArea>
          <OvRecordDetailsSection title={t('scanList.details.toolkitSection')}>
            <StyledAccordion>
              <StyledAccordionSummary
                expandIcon={<ExpandMoreIcon />}
                aria-controls="toolkit-input"
                id="toolkit-input"
              >
                <StyledTitle>Toolkit Input</StyledTitle>
              </StyledAccordionSummary>
              <AccordionDetails>
                <JsonContainer>
                  <JSONInput
                    id="toolkit_input_editor"
                    placeholder={scan.toolkit_input || {}}
                    locale={locale}
                    theme="light_mitsuketa_tribute"
                    height="600px"
                    width="100%"
                    viewOnly={true}
                  />
                </JsonContainer>
              </AccordionDetails>
            </StyledAccordion>

            <StyledAccordion>
              <StyledAccordionSummary
                expandIcon={<ExpandMoreIcon />}
                aria-controls="toolkit-output"
                id="toolkit-output"
              >
                <StyledTitle>Toolkit Output</StyledTitle>
              </StyledAccordionSummary>
              <AccordionDetails>
                <JsonContainer>
                  <JSONInput
                    id="toolkit_output_editor"
                    placeholder={scan.toolkit_output || {}}
                    locale={locale}
                    theme="light_mitsuketa_tribute"
                    height="600px"
                    width="100%"
                    viewOnly={true}
                  />
                </JsonContainer>
              </AccordionDetails>
            </StyledAccordion>
          </OvRecordDetailsSection>
        </ToolkitArea>
      </OvRecordDetailsCanvas>

      {isLoading && <OvLoadingIndicator position="fixed" />}
    </Page>
  );
};

export default ScanDetails;

const Page = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1.375rem;
  margin-left: 1rem;
  min-width: 0;
  color: ${Colours.OV_BASE};
`;

const StateActions = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
`;

const ToolkitVersionField = styled(Autocomplete)`
  min-width: 10rem;
` as typeof Autocomplete;

const StyledAlert = styled(Alert)`
  && {
    margin: 1rem 1.5rem 0;
    border-radius: ${Variables.borderRadius.LARGE};
    background-color: ${Colours.OV_YELLOW};
    color: ${Colours.OV_BASE};

    .MuiAlertTitle-root {
      font-weight: bold !important;
    }
  }
`;

const ToolkitArea = styled.div`
  border-top: 1px solid ${Colours.OV_BORDER_COLOR_V3};
`;

const StyledAccordion = styled(Accordion)`
  && {
    box-shadow: none;
    margin-bottom: 1rem;
  }
`;

const StyledAccordionSummary = styled(AccordionSummary)`
  && {
    background-color: ${Colours.OV_LIGHT_GRAY};
    border-radius: 0.25rem;
    align-items: center;

    .MuiAccordionSummary-content {
      align-items: center;
    }
  }
`;

const StyledTitle = styled.span`
  font-size: ${Variables.fontSizes.MEDIUM};
  font-weight: bold;
`;

const JsonContainer = styled.div`
  padding-bottom: 1.5rem;
`;
